"use client";

import { useState, useEffect, useCallback } from "react";
import { getPublicInfo, putInfo } from "utils/api";
import { extractErrorMessage, Severity } from "utils/helper/ApiResponses";
import { YouTubeUrl } from "utils/types/Info.types";


interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
}

export function useYoutubeUrlManager() {
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // --- Snackbar Handlers ---
  const handleSnackbarOpen = useCallback((message: string, severity: Severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Data Fetching ---
  const fetchYoutubeUrl = useCallback(async () => {
    setInitialLoading(true);
    try {
      // Use the new getPublicInfo service
      const result = await getPublicInfo<YouTubeUrl>(['youtubeUrl']);
      
      // Access the data from the 'data' property
      const data = result.data as YouTubeUrl | null;
      if (result.status === 'success' && data?.youtubeUrl) {
        setYoutubeUrl(data.youtubeUrl);
      }
    } catch (error: any) {
      console.error("Error fetching YouTube URL:", error);
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setInitialLoading(false);
    }
  }, [handleSnackbarOpen]);

  // Fetch the URL when the component mounts
  useEffect(() => {
    fetchYoutubeUrl();
  }, [fetchYoutubeUrl]);

  // --- Data Saving ---
  const handleSaveYoutubeUrl = async () => {
    setIsSaving(true);
    try {
      // Use the new putInfo service
      // The slug is ['youtubeUrl']
      // The payload is the object to save
      await putInfo(['youtubeUrl'], { youtubeUrl: youtubeUrl });
      
      handleSnackbarOpen("YouTube URL updated successfully!", "success");
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
      console.error("Error saving YouTube URL:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    youtubeUrl,
    setYoutubeUrl,
    initialLoading,
    isSaving,
    snackbar,
    handleSaveYoutubeUrl,
    handleSnackbarClose,
  };
}